import React from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  LinearProgress,
} from '@material-ui/core';
import { ResourceMetrics } from '../types';

interface KubernetesResourceMetricsCardProps {
  metrics: ResourceMetrics | null;
  loading?: boolean;
}

function formatCpu(millicores: number): string {
  if (millicores >= 1000) return `${(millicores / 1000).toFixed(2)} cores`;
  return `${millicores}m`;
}

function formatMemory(bytes: number): string {
  const units = ['B', 'Ki', 'Mi', 'Gi'];
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) { value /= 1024; i++; }
  return `${value.toFixed(0)} ${units[i]}`;
}

function getBarColor(pct: number): 'primary' | 'secondary' {
  return pct >= 80 ? 'secondary' : 'primary';
}

interface MetricRowProps {
  label: string;
  percentage: number;
  usage: string;
  limit: string;
}

const MetricRow: React.FC<MetricRowProps> = ({ label, percentage, usage, limit }) => {
  const pct = Math.min(percentage, 100);
  const isHigh = pct >= 80;
  return (
    <Box mb={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Typography variant="body2" style={{ fontWeight: 500, fontSize: '0.82rem' }}>{label}</Typography>
        <Typography
          variant="body2"
          style={{ fontSize: '0.8rem', color: isHigh ? '#d32f2f' : '#555', fontWeight: isHigh ? 600 : 400 }}
        >
          {usage} / {limit} ({pct.toFixed(0)}%)
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        color={getBarColor(pct)}
        style={{ height: 6, borderRadius: 3 }}
      />
    </Box>
  );
};

export const KubernetesResourceMetricsCard: React.FC<KubernetesResourceMetricsCardProps> = ({
  metrics,
  loading,
}) => {
  return (
    <Card>
      <CardHeader
        title="资源使用"
        titleTypographyProps={{ variant: 'subtitle1', style: { fontWeight: 600 } }}
        style={{ paddingBottom: 0 }}
      />
      <CardContent>
        {loading ? (
          <Typography variant="body2" color="textSecondary">加载中...</Typography>
        ) : !metrics ? (
          <Typography variant="body2" color="textSecondary">
            暂无指标数据（需要 metrics-server）
          </Typography>
        ) : (
          <>
            <MetricRow
              label="CPU"
              percentage={metrics.cpu.percentage}
              usage={formatCpu(metrics.cpu.usage)}
              limit={formatCpu(metrics.cpu.limits)}
            />
            <MetricRow
              label="内存"
              percentage={metrics.memory.percentage}
              usage={formatMemory(metrics.memory.usage)}
              limit={formatMemory(metrics.memory.limits)}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};
